import { Check } from 'lucide-react';
import type { TaskDto } from '@werft/contracts';
import { t } from '../../i18n/index.js';
import { useRights } from '../../platform/rights.js';
import { setCompleted } from './taskActions.js';
import { latestDone, trackDone } from './linger.js';

/**
 * Auftrag „Projektinsel“ W02: ein Erledigt-Zustand, in Liste, Board und Tabelle gleich bedient. Bedienbar nur mit
 * Bearbeitungsrecht – sonst eine reine Anzeige (Häkchen bzw. nichts), keine gesperrte Checkbox, die bedienbar aussieht.
 * Abschluss und Wiederöffnen laufen über dieselbe Fachlogik (setCompleted); Wiederöffnen gegen den bestätigten Stand.
 * Klicks erreichen weder Öffnen noch Ziehen der Zeile/Karte.
 */
export function toggleDone(task: TaskDto, before?: () => void) {
  if (!task.completedAt) {
    before?.();
    const r = setCompleted(task, true);
    trackDone(task.id, r);
    void r.catch(() => {});
  } else {
    void latestDone(task).then((fresh) => setCompleted(fresh, false)).catch(() => {});
  }
}

export function DoneMark({ task, className = 'size-11' }: { task: TaskDto; className?: string }) {
  const done = !!task.completedAt;
  return (
    <span data-done-state="" role="img" aria-label={t(done ? 'task.stateDone' : 'task.stateOpen', { title: task.title })} className={`flex shrink-0 items-center justify-center ${className}`}>
      {done && <Check className="size-4 text-emerald-700 dark:text-emerald-400" aria-hidden />}
    </span>
  );
}

/** Tabelle: rundes Erledigt-Feld, optisch und im Namen klar von der eckigen Mehrfachauswahl getrennt. */
export function DoneToggle({ task }: { task: TaskDto }) {
  const rights = useRights();
  const done = !!task.completedAt;
  if (!rights.execute) return <DoneMark task={task} className="size-9" />;
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={done}
      data-done=""
      disabled={task.id.startsWith('tmp-')}
      onClick={(e) => {
        e.stopPropagation();
        toggleDone(task);
      }}
      onPointerDown={(e) => e.stopPropagation()}
      aria-label={t('task.toggle', { title: task.title })}
      className="grid size-9 place-items-center rounded-full outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent)]"
    >
      <span aria-hidden className={`grid size-5 place-items-center rounded-full border-2 ${done ? 'border-emerald-600 bg-emerald-600 text-white' : 'border-zinc-400 hover:border-emerald-600 dark:border-zinc-500'}`}>
        {done && <Check className="size-3.5" strokeWidth={3} />}
      </span>
    </button>
  );
}
